import { type SearchBox, searchBoxes, suggestionsFor } from "./search-box.js";
import { RouteSuggestions } from "./route-suggestions.js";
import type { SuggestionWriting } from "../site-search/search-paths.js";
import { SuggestionList } from "./suggestion-list.js";
import { steerList } from "./suggestion-keys.js";
import { wordAt } from "./suggestion-option.js";

/**
 * Offering a reader the word they are typing, before they reach the button.
 *
 * Every search costs a round trip. Somebody typing 人 waits for the button
 * before anything happens, where the site already knows 人才, 人们 and 人口.
 * This says so, out of the search route the button would have reached.
 *
 * **Nothing here is required, and that is the whole design of the route.** The
 * form is a plain `<form method="get">` with a plain `<input>` in it, drawn by
 * `partials/search-form.html` and again by `src/site-search/search-markup.ts`,
 * and it submits a query string to an address that answers it. A reader whose
 * script never arrives, or whose request never arrives, sees exactly that page.
 * What this adds is a list. Picking from the list fills the box and submits the
 * form, so a suggestion is a second way into the route and never a second
 * answer to the query. `docs/search.md` has why the backend came first.
 *
 * **A keystroke costs a request, where it used to cost nothing.** The words
 * were a published file until [#683](https://github.com/KensioSoftware/chineseboost/issues/683),
 * fetched whole on the first keystroke and answered off the disk after that,
 * and the file grew with the section. Three things stand between a reader and
 * a request per character: the list settles before anything is asked for, a
 * prefix this page has already asked about is drawn without asking again, and
 * the route's answers are cacheable, so the browser holds the prefixes shared
 * by the last word this reader typed. `./route-suggestions.ts` holds the last
 * two.
 *
 * **What the box does while it waits is nothing.** The list a reader was
 * already looking at stays where it is until there is a better one, which is
 * what a list that emptied itself on every keystroke would take away from
 * them. An answer that arrives for a prefix the reader has since typed past is
 * dropped rather than drawn.
 *
 * **A page can carry two boxes and they share one page's worth of answers.**
 * The rail draws one on every 词典 and 例句 page and the article draws another,
 * and both are the same control asking the same route. `RouteSuggestions` is
 * what they share.
 */

/**
 * How long the box has to be still before anything is asked for.
 *
 * **Measured against a reader rather than chosen for the look of it.** A
 * competent typist puts 150 to 200 milliseconds between two characters and a
 * phone keyboard nearer 300, so a wait of this length asks about the word
 * somebody stopped on rather than about every prefix they passed through. It
 * is also under the quarter of a second at which a list stops feeling like it
 * was already there.
 *
 * A prefix this page has already asked about skips the wait entirely, which is
 * what a reader backspacing over a word sees. See `RouteSuggestions.offered`.
 */
const settleTime = 200;

/** How many suggestions a list holds, however many the route sent. */
const mostSuggestions = 8;

/** One search box, wired to the route behind it. */
class SearchSuggestions {
  readonly #box: SearchBox;
  readonly #list: SuggestionList;
  readonly #source: RouteSuggestions;
  #settling: ReturnType<typeof setTimeout> | undefined;
  #composing = false;

  constructor(box: SearchBox, at: number, source: RouteSuggestions) {
    this.#box = box;
    this.#source = source;
    this.#list = new SuggestionList({
      around: box.around,
      input: box.input,
      box: at,
    });
  }

  /** Listen for everything that changes what should be on offer. */
  start(): void {
    this.#box.input.addEventListener("input", () => {
      this.#offer();
    });

    /*
     * An IME writes into the box as it goes, so a reader typing 人 through one
     * puts `r`, `re` and `ren` in it before they put 人 there. A list moving
     * under somebody who is reading their own candidate window is no help to
     * them, so this waits for the character. It also spends no requests on the
     * letters, which were never the question.
     */
    this.#box.input.addEventListener("compositionstart", () => {
      this.#composing = true;
    });

    this.#box.input.addEventListener("compositionend", () => {
      this.#composing = false;
      this.#offer();
    });

    this.#box.input.addEventListener("keydown", (event) => {
      steerList({ event, list: this.#list, input: this.#box.input });
    });

    /*
     * `mousedown` rather than `click`, and its default refused. A click on the
     * list would take focus off the box first, and the box losing focus is
     * what puts the list away, so by the time the click arrived there would be
     * nothing under the pointer.
     */
    this.#box.around.addEventListener("mousedown", (event) => {
      const word = wordAt(event.target);

      if (word !== undefined) {
        event.preventDefault();
        this.#search(word);
      }
    });

    this.#box.around.addEventListener("focusout", (event) => {
      const moved = event.relatedTarget;

      if (!(moved instanceof Node) || !this.#box.around.contains(moved)) {
        this.#list.hide();
      }
    });
  }

  /** Draw what is known for whatever is in the box now, or go and ask. */
  #offer(): void {
    const query = this.#box.input.value.trim();

    if (this.#composing) {
      return;
    }

    this.#settle();

    if (query === "") {
      this.#list.hide();

      return;
    }

    const writing = suggestionsFor(this.#box);
    const known = this.#source.offered(writing, query);

    if (known === undefined) {
      this.#settling = setTimeout(() => {
        void this.#ask(writing, query);
      }, settleTime);
    } else {
      this.#list.show(known.slice(0, mostSuggestions));
    }
  }

  /**
   * Ask the route, and draw the answer if it is still the question.
   *
   * **Both halves of the question are checked**, because either can move while
   * a request is in flight. The reader has kept typing, and they may also have
   * changed the writing they are shown, which `reader-settings.scss` acts on
   * without touching the box. An answer for the writing they have left would
   * put the other script in the list.
   */
  async #ask(writing: SuggestionWriting, query: string): Promise<void> {
    const found = await this.#source.offer(writing, query);

    if (
      this.#box.input.value.trim() === query &&
      suggestionsFor(this.#box) === writing
    ) {
      this.#list.show(found.slice(0, mostSuggestions));
    }
  }

  /** Forget a request that was about to be made for what was in the box. */
  #settle(): void {
    clearTimeout(this.#settling);
    this.#settling = undefined;
  }

  /** Put a word in the box and search for it, as the button would. */
  #search(word: string): void {
    this.#box.input.value = word;
    this.#list.hide();
    this.#box.form.requestSubmit();
  }
}

/**
 * Wire every search box on this page, where there is one to wire.
 *
 * Wires nothing on a page with no box, which is what makes this safe to load
 * from the one partial that draws them. The partial is called twice on a 词典
 * or 例句 page, once for the rail and once for the article, so the script tag
 * it writes is on the page twice and a browser evaluates the module once.
 */
export function startSearchSuggestions(page: Document): void {
  const source = new RouteSuggestions();

  for (const [at, box] of searchBoxes(page).entries()) {
    new SearchSuggestions(box, at, source).start();
  }
}
